class StatCounter extends HTMLElement {
  connectedCallback() {
    this.targetValue = this.#parseNumber(this.dataset.target);
    this.duration = Number(this.dataset.duration) || 2000;
    this.useSeparator = this.dataset.thousandSeparator !== 'false';
    this.decimals = Number(this.dataset.decimals) || 0;
    this.startValue = 0;
    this.animated = false;

    if (this.dataset.enabled === 'false' || !Number.isFinite(this.targetValue)) {
      this.#render(this.targetValue);
      return;
    }

    this.#render(this.startValue);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#render(this.targetValue);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !this.animated) {
            this.animated = true;
            this.#animate();
            this.#observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.25 }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  #observer;

  #parseNumber(raw) {
    if (raw == null) return 0;
    const cleaned = String(raw).replace(/[^0-9.\-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  }

  #format(value) {
    const rounded = this.decimals > 0
      ? Number(value.toFixed(this.decimals))
      : Math.round(value);

    if (!this.useSeparator) return String(rounded);

    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: this.decimals,
      maximumFractionDigits: this.decimals,
    });
  }

  #render(value) {
    this.textContent = this.#format(value);
  }

  #animate() {
    const startTime = performance.now();
    const step = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / this.duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = this.startValue + (this.targetValue - this.startValue) * eased;
      this.#render(current);
      if (progress < 1) {
        requestAnimationFrame(step);
      } else {
        this.#render(this.targetValue);
      }
    };
    requestAnimationFrame(step);
  }
}

if (!customElements.get('stat-counter')) {
  customElements.define('stat-counter', StatCounter);
}
